import { FC, useEffect, useState } from 'react';
import Spinner from 'design-system/Spinner';
import apiFetch from '@scale/frontend-lib/api-fetch';
import PipelineNodeGraph from './PipelineNodeGraph';
import { PipelineHistory } from '@scale/shared/pipelinev3/lookup';
import { Checkbox } from 'design-system';
import { makeStyles } from 'design-system/styles';
import SmartLink from 'corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { formatDateWithTz } from 'react-scale/corp/components/Lookup/sections/ObjectDetails';

interface IPipelineHistoryGraphProps {
  // Can be either a taskId or a root node Id
  id: string;
  showSkipNode?: boolean;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  leftColumn: {
    flexBasis: '60%',
    padding: '0px',
  },
  rightColumn: {
    flexBasis: '40%',
    padding: '0px',
  },
});

const getIdsToHighlight = (data: PipelineHistory, execIds: string[]) => {
  const nodeIds = [];
  const resourceIds = [];
  for (const exec of data.executions) {
    if (execIds.includes(exec.id)) {
      nodeIds.push(...exec.nodeIds);
    }
  }
  for (const node of data.nodes) {
    if (nodeIds.includes(node.id)) {
      resourceIds.push(...node.inputResourceIds);
      resourceIds.push(...node.outputResourceIds);
    }
  }
  return [...new Set([...nodeIds, ...resourceIds])];
};

const PipelineHistoryGraph: FC<IPipelineHistoryGraphProps> = ({ id, showSkipNode = false }) => {
  const [data, setData] = useState<PipelineHistory | null>(null);
  const [rowSelection, setRowSelection] = useState<Set<string>>(new Set());
  const classes = useStyles();

  useEffect(() => {
    async function fetchData() {
      const response: PipelineHistory = await apiFetch(
        null,
        `corp-api/lookup/${id}/pipelineHistory`,
      );
      setData(response);
      const nonInvalidatedExecution = response.executions.find(e => e.status !== 'invalidated')?.id;
      if (nonInvalidatedExecution) {
        setRowSelection(new Set([nonInvalidatedExecution]));
      }
    }
    fetchData();
  }, [id]);

  return (
    <div>
      {data ? (
        <div className={classes.container}>
          <div className={classes.leftColumn}>
            <PipelineNodeGraph
              nodes={data.nodes}
              highlightIds={getIdsToHighlight(data, [...rowSelection])}
              height={'800px'}
              showSkipNode={showSkipNode}
              executionId={rowSelection.size > 0 ? Array.from(rowSelection)[0] : undefined}
              selectedExecutionCount={rowSelection.size}
            ></PipelineNodeGraph>
          </div>
          <div className={classes.rightColumn}>
            <span style={{ fontSize: '18px' }}>Executions (truncated to last 25):</span>
            <table>
              <thead>
                <tr>
                  <th style={{ padding: '0px 0px' }}>Highlight</th>
                  <th style={{ padding: '0px 0px' }}>Execution</th>
                  <th style={{ padding: '0px 0px' }}>Created At</th>
                  <th style={{ padding: '0px 0px' }}>Caused By</th>
                </tr>
              </thead>
              <tbody>
                {data.executions.map(exec => (
                  <tr key={exec.id}>
                    <td style={{ textAlign: 'center' }}>
                      <Checkbox
                        label={''}
                        checked={rowSelection.has(exec.id)}
                        onChange={() => {
                          const newRowSelection = new Set(rowSelection);
                          if (rowSelection.has(exec.id)) {
                            newRowSelection.delete(exec.id);
                          } else {
                            newRowSelection.add(exec.id);
                          }
                          setRowSelection(newRowSelection);
                        }}
                      />
                    </td>
                    <td>
                      <a href={`/corp/lookup/${exec.id}`}>
                        <span>{exec.id}</span>
                      </a>
                    </td>
                    <td>
                      {formatDateWithTz(exec.createdAt, {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                    {exec.causedByIntervention && (
                      <td style={{ textAlign: 'center' }}>
                        <SmartLink
                          key={exec.causedByIntervention.id}
                          id={exec.causedByIntervention.id}
                          type={INamedObjectTypes.PipelineV3ManualIntervention}
                          showLogo={false}
                          linkText={exec.causedByIntervention.traitName ?? 'fixedIntervention'}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <style jsx>{`
              th,
              td {
                padding-left: 6px;
                padding-right: 6px;
              }
            `}</style>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export default PipelineHistoryGraph;
